import { type ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { type Tone } from "@/lib/status";
import { cn } from "@/lib/cn";

/** Map a semantic tone to the value-text color. `undefined` keeps fg-primary. */
const valueToneClass: Record<Tone, string> = {
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  neutral: "text-fg-muted",
  accent: "text-accent",
};

export interface KPICardProps {
  /** Label shown above the value. */
  label: ReactNode;
  /** Big mono value. */
  value: ReactNode;
  /** Color the value by tone (success / danger / …). Defaults to fg-primary. */
  tone?: Tone;
  /** Optional small hint under the value. */
  hint?: ReactNode;
  /** Optional signed delta chip (e.g. "+12"). Positive = success, negative = danger. */
  delta?: number;
  /** Render the value bigger (hero KPI). */
  hero?: boolean;
  /** Make the whole card a click-through (e.g. to a filtered list). */
  onClick?: () => void;
  /** Accessible label for the click-through card. */
  ariaLabel?: string;
  className?: string;
}

/**
 * KPICard — value (mono) + label + optional delta + optional click-through
 * (DESIGN-SYSTEM §5). Unifies the dashboard overview tiles and analytics KPIs.
 */
export function KPICard({
  label,
  value,
  tone,
  hint,
  delta,
  hero = false,
  onClick,
  ariaLabel,
  className,
}: KPICardProps) {
  const interactive = !!onClick;
  const valueClass = tone ? valueToneClass[tone] : "text-fg-primary";

  const body = (
    <>
      <p className="truncate text-xs font-medium text-fg-muted">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span
          className={cn(
            "font-mono font-semibold tabular-nums leading-none",
            hero ? "text-4xl" : "text-2xl",
            valueClass,
          )}
        >
          {value}
        </span>
        {typeof delta === "number" && delta !== 0 && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-medium",
              delta > 0 ? "text-success" : "text-danger",
            )}
          >
            {delta > 0 ? (
              <ArrowUpRight size={13} strokeWidth={2} aria-hidden="true" />
            ) : (
              <ArrowDownRight size={13} strokeWidth={2} aria-hidden="true" />
            )}
            {Math.abs(delta)}
          </span>
        )}
      </div>
      {/* Hints are full sentences ("потрачено / успешные доставки"); truncating
          them to one line cut most of them mid-word. Let them wrap — the grid
          row stretches all cards to the tallest one anyway. */}
      {hint && <p className="mt-1 text-xs leading-snug text-fg-muted">{hint}</p>}
    </>
  );

  if (interactive) {
    return (
      <Card interactive className={cn("p-4", className)}>
        <button
          type="button"
          onClick={onClick}
          aria-label={ariaLabel}
          className="block w-full text-left focus-visible:outline-none"
        >
          {body}
        </button>
      </Card>
    );
  }

  return <Card className={cn("p-4", className)}>{body}</Card>;
}
KPICard.displayName = "KPICard";
